'use client'

import React, { useEffect, useState } from 'react';
import Image from 'next/image';
import Masonry, { ResponsiveMasonry } from 'react-responsive-masonry';

export default function MasonryGallery({ items, year }) {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [activeModal, setActiveModal] = useState(false);
  const [loadedImages, setLoadedImages] = useState({});
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    if (!activeModal) {
      return undefined;
    }

    function handleKeyDown(event) {
      if (event.key === 'Escape') {
        setActiveModal(false);
        return;
      }

      if (event.key === 'ArrowLeft') {
        handleMovePrev();
        return;
      }

      if (event.key === 'ArrowRight') {
        handleMoveNext();
      }
    }

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [activeModal, items.length]);

  const handleMovePrev = () => {
    setCurrentImageIndex((prevIndex) => (prevIndex + items.length - 1) % items.length);
  };

  const handleMoveNext = () => {
    setCurrentImageIndex((prevIndex) => (prevIndex + 1) % items.length);
  };

  const handleImageClick = (index) => {
    setCurrentImageIndex(index);
    setActiveModal(true);
  };

  const handleImageLoaded = (index) => {
    setLoadedImages((current) => {
      if (current[index]) return current;
      return { ...current, [index]: true };
    });
  };

  const currentItem = items[currentImageIndex];

  const galleryItems = items.map((item, index) => (
    <div className="spacing picture-item" key={`${item.image}-${index}`}>
      <div className="card portfolio portfolio-modern portfolio-primary rounded overflow-hidden">
        <button
          type="button"
          onClick={() => handleImageClick(index)}
          className="lightbox border-0 p-0 bg-white w-100 text-start"
        >
          <div className="card-img position-relative overflow-hidden bg-light">
            {!loadedImages[index] && (
              <div className="placeholder-glow position-absolute top-0 start-0 w-100 h-100 p-3">
                <span className="placeholder w-100 rounded" style={{ height: '320px' }}></span>
              </div>
            )}
            <Image
              src={item.image}
              width={720}
              height={1080}
              sizes="(max-width: 575px) 100vw, (max-width: 767px) 50vw, (max-width: 991px) 33vw, 25vw"
              loading="lazy"
              style={{
                width: '100%',
                height: 'auto',
                opacity: loadedImages[index] ? 1 : 0,
                transition: 'opacity 0.25s ease',
                display: 'block',
              }}
              className="img-fluid"
              alt={item.alt}
              onLoad={() => handleImageLoaded(index)}
            />
          </div>
        </button>
        <div className="content text-center p-3">
          {loadedImages[index] ? (
            <>
              <button
                type="button"
                onClick={() => handleImageClick(index)}
                className="text-dark h6 mb-0 d-block title border-0 bg-transparent w-100 p-0"
              >
                {item.title}
              </button>
              <small className="text-muted fw-normal mb-0">Miss Plaja {year}</small>
            </>
          ) : (
            <div className="placeholder-glow">
              <span className="placeholder col-8 mb-2"></span>
              <span className="placeholder col-5"></span>
            </div>
          )}
        </div>
      </div>
    </div>
  ));

  return (
    <>
      <div className="row justify-content-center mt-4">
        <div className="col-lg-12">
          {mounted ? (
            <ResponsiveMasonry columnsCountBreakPoints={{ 350: 1, 576: 2, 767: 3, 991: 4 }}>
              <Masonry>{galleryItems}</Masonry>
            </ResponsiveMasonry>
          ) : (
            <div className="row g-3">
              {galleryItems.map((item) => (
                <div className="col-12 col-sm-6 col-md-4 col-lg-3" key={item.key}>
                  {item}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {activeModal && currentItem ? (
        <div
          className="modal fade show"
          style={{ display: 'block', backgroundColor: 'rgba(0, 0, 0, 0.82)' }}
          role="dialog"
          aria-modal="true"
          onClick={() => setActiveModal(false)}
        >
          <div
            className="modal-dialog modal-xl modal-dialog-centered"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="modal-content border-0 shadow-lg overflow-hidden">
              <div className="modal-header">
                <h5 className="modal-title">
                  {currentItem.title} · {currentImageIndex + 1} / {items.length}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  aria-label="Close"
                  onClick={() => setActiveModal(false)}
                />
              </div>
              <div className="modal-body p-0 bg-dark position-relative">
                {items.length > 1 ? (
                  <button
                    type="button"
                    className="btn btn-light position-absolute top-50 start-0 translate-middle-y ms-3"
                    onClick={handleMovePrev}
                    aria-label="Imaginea anterioara"
                  >
                    {'<'}
                  </button>
                ) : null}

                <img
                  src={currentItem.image}
                  alt={currentItem.alt}
                  className="w-100"
                  style={{ maxHeight: '85vh', objectFit: 'contain', display: 'block' }}
                />

                {items.length > 1 ? (
                  <button
                    type="button"
                    className="btn btn-light position-absolute top-50 end-0 translate-middle-y me-3"
                    onClick={handleMoveNext}
                    aria-label="Imaginea urmatoare"
                  >
                    {'>'}
                  </button>
                ) : null}
              </div>
              <div className="modal-footer justify-content-between">
                <span className="text-muted small">
                  Titlu: {currentItem.title}
                </span>
                <span className="text-muted small">
                  Galerie homepage
                </span>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}
